import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { FullProjectInvoice } from 'entities';

import { Column, Row, Modal } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { PropertyWithIcon } from 'components/PropertyWithIcon';

import {
  TrendingDownIcon,
  LocationPinIcon,
  TagIcon,
  DollarBillIcon,
  InfoIcon,
  UserSquareIcon,
} from 'components/Icons';

import { ProjectInvoiceMenu } from './Menu';

import { formatCurrency } from 'utils/text';

interface InfoModalProps {
  children?: never;
  projectInvoice: FullProjectInvoice | null;
  close: () => void;
}

const projectInvoiceProps: Array<{
  getName: (t: TFunction) => string;
  extractor: (projectInvoice: FullProjectInvoice) => string;
  shouldRender?: (projectInvoice: FullProjectInvoice) => boolean;
  Icon: typeof LocationPinIcon;
}> = [
  {
    getName: (t: TFunction) => t('country'),
    extractor: ({ country }) => country,
    Icon: LocationPinIcon,
  },
  {
    getName: (t: TFunction) => t('client'),
    extractor: ({ clientName }) => clientName,
    Icon: UserSquareIcon,
  },
  {
    getName: (t: TFunction) => t('value-in-project-currency'),
    extractor: ({ projectCurrency, valueInProjectCurrency }) =>
      formatCurrency(valueInProjectCurrency, projectCurrency),
    Icon: DollarBillIcon,
  },
  {
    getName: (t: TFunction) => t('value'),
    extractor: ({ currency, value }) => formatCurrency(value, currency),
    Icon: DollarBillIcon,

    shouldRender: ({ quotationRequest }: FullProjectInvoice) => !!quotationRequest,
  },
  {
    getName: (t: TFunction) => t('discount'),

    extractor: ({ discount, discountValue, currency }) =>
      `${formatCurrency(discountValue ?? '0.00', currency)} (${discount}%)`,

    Icon: TrendingDownIcon,

    shouldRender: ({ discount }: FullProjectInvoice) => !!discount,
  },
  {
    getName: (t: TFunction) => t('sample-ids'),
    extractor: ({ sampleIds }) => sampleIds.join(', '),
    Icon: TagIcon,
  },
  {
    getName: (t: TFunction) => t('extra-information'),
    extractor: ({ extraInformation }) => extraInformation,
    Icon: InfoIcon,
  },
];

export function InfoModal({ projectInvoice, close }: InfoModalProps) {
  const { t } = useLang();

  return (
    <Modal
      preventOpenAutoFocus
      allowEscape
      width="min(90vw, 650px)"
      isOpen={!!projectInvoice}
      close={close}
      gap="5"
      title={
        <Row gap="2">
          <h2 className="text-lg font-semibold leading-none tracking-tight">
            {t('project-invoice')}
          </h2>

          {!!projectInvoice && (
            <>
              <EntityStatusTag
                target="projectInvoice"
                status={projectInvoice.status}
                size="medium"
                round={false}
              />

              <ProjectInvoiceMenu projectInvoice={projectInvoice} />
            </>
          )}
        </Row>
      }
    >
      {projectInvoice && (
        <>
          <Column>
            {projectInvoiceProps.map(
              ({ Icon, getName, extractor, shouldRender }) =>
                (shouldRender?.(projectInvoice) ?? true) && (
                  <PropertyWithIcon
                    key={`invoice-info-${getName(t)}`}
                    icon={<Icon />}
                    name={getName(t)}
                    value={extractor(projectInvoice)}
                  />
                ),
            )}
          </Column>
        </>
      )}
    </Modal>
  );
}
